/**
 * Reading flow — pragmatic single-page state machine.
 *
 * States:
 *   pick-mentor → pick-category → write-intention → drawing → streaming → done
 *
 * Streaming branch covers `mode='streaming'` mentors (most). Async
 * mentors fall back to a "submitted, come back later" success state —
 * polling lives on the History page (FE-8).
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { api, formatApiError, makeSSEAbort, parseSSEEvents, type Category, type DrawnCard, type Mentor, type SessionState, type Spread } from '../api';
import { track } from '../analytics';
import { useAuth } from '../auth';
import { useI18n } from '../i18n';
import CheckinPill from '../components/CheckinPill';
import Disclaimer from '../components/Disclaimer';
import DailyOmen from '../components/DailyOmen';
import { paymentsEnabled } from '../config';
import { Loading, Section } from '../components/reading/_ui';
import { PickCarousel } from '../components/reading/PickCarousel';
import { MentorCard, MentorFilterChips, SpreadPositions } from '../components/reading/MentorPicker';
import { FollowupPanel } from '../components/reading/FollowupPanel';
import { RatingPanel } from '../components/reading/RatingPanel';

type Step =
  | 'pick-mentor'
  | 'pick-spread'      // STA-147 — choose 1/3/4/10-card spread
  | 'pick-category'
  | 'write-intention'
  | 'centering'      // meditation interstitial — user closes eyes, taps 開始抽牌
  | 'opening'        // server allocating pool
  | 'pick-cards'     // STA-116 — N face-down placeholders, user taps K of them
  | 'committing'    // user pressed 解讀; waiting for picks_committed event
  | 'streaming'
  | 'done'
  | 'rate-limited';

export default function Reading() {
  const { user, refresh } = useAuth();
  const { t, lang } = useI18n();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const replaySessionId = searchParams.get('session');
  const [step, setStep] = useState<Step>(replaySessionId ? 'opening' : 'pick-mentor');
  const [mentors, setMentors] = useState<Mentor[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  // STA-152 — spreads loaded from /spreads instead of hardcoded mocks.
  const [spreads, setSpreads] = useState<Spread[]>([]);
  // m1 — distinguish "still loading catalog" from "loaded empty catalog"
  // so the pick-mentor view shows a spinner instead of a blank page
  // during the initial fetch.
  const [catalogLoading, setCatalogLoading] = useState(true);
  const [mentorId, setMentorId] = useState<string | null>(null);
  // STA-147/152 — selected spread (defaults to three_card if present in
  // the API response; otherwise the first available row).
  const [spreadId, setSpreadId] = useState<string>('three_card');
  const [categoryId, setCategoryId] = useState<string | null>(null);
  const [intention, setIntention] = useState('');
  const [session, setSession] = useState<SessionState | null>(null);
  const [streamText, setStreamText] = useState('');
  const [error, setError] = useState<string | null>(null);
  // M3 — surface a non-fatal warning when /complete fails after a
  // successful stream (or when replaying a session that never reached
  // status='complete'). User still sees the interpretation; the warning
  // hints why follow-ups / rating might temporarily 409. RatingPanel
  // already self-heals via its own retry, so we keep it mounted.
  const [completeWarning, setCompleteWarning] = useState<string | null>(null);
  // STA-146 — filter mentors by category chip.
  const [mentorFilterTag, setMentorFilterTag] = useState<string | null>(null);
  // STA-116 — interactive draw state
  const [interactiveSessionId, setInteractiveSessionId] = useState<string | null>(null);
  const [poolSize, setPoolSize] = useState(0);
  const [requiredPicks, setRequiredPicks] = useState(3);
  const [pickedSlots, setPickedSlots] = useState<number[]>([]);
  const [revealedCards, setRevealedCards] = useState<DrawnCard[] | null>(null);
  const [revealedIdx, setRevealedIdx] = useState<Set<number>>(new Set());

  // STA-122 — follow-up state (Reading Economy v2 §F9)
  const [followupDraft, setFollowupDraft] = useState('');
  const [followupBusy, setFollowupBusy] = useState(false);
  const [followupStreamText, setFollowupStreamText] = useState('');
  const [followupError, setFollowupError] = useState<string | null>(null);
  // Re-render every 30s so the follow-up countdown stays fresh.
  const [, setNowTick] = useState(0);

  // M4 — track active SSE controllers so unmount / route change can
  // abort them. A second SSE start (e.g. user retries) bumps the prior
  // one out via cancel(). Stored as refs to dodge the state churn that
  // would re-render mid-stream.
  const commitSSERef = useRef<ReturnType<typeof makeSSEAbort> | null>(null);
  const followupSSERef = useRef<ReturnType<typeof makeSSEAbort> | null>(null);

  useEffect(() => {
    // Cleanup any in-flight stream when the component unmounts (route
    // change). Without this, fetch keeps the connection open until the
    // server cuts it, leaking memory and potentially still calling
    // setState on an unmounted component.
    return () => {
      commitSSERef.current?.cancel();
      followupSSERef.current?.cancel();
    };
  }, []);

  const mentor = useMemo(
    () => mentors.find((m) => m.id === mentorId) ?? null,
    [mentors, mentorId],
  );

  // Tick every 30s during the follow-up window so the countdown
  // re-renders. Only mounted on step='done' to avoid leaking timers.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (step !== 'done') return;
    const id = setInterval(() => setNowTick((n) => n + 1), 30_000);
    return () => clearInterval(id);
  }, [step]);

  useEffect(() => {
    setCatalogLoading(true);
    void Promise.all([api.mentors(), api.categories(), api.spreads()])
      .then(([m, c, s]) => {
        setMentors(m);
        setCategories(c);
        setSpreads(s);
        // Keep three_card as default when present; otherwise fall back
        // to whatever the server returns first so an admin who only
        // ships single-card decks still sees the picker work.
        if (s.length > 0 && !s.find((x) => x.id === 'three_card')) {
          setSpreadId(s[0].id);
        }
      })
      .catch((e) => setError(formatApiError(e, '載入導師清單失敗，請重新整理。')))
      .finally(() => setCatalogLoading(false));
  }, []);

  // Same-route nav clicks (e.g. tapping `抽牌` while already on /reading) don't
  // remount this component, so without help the user stays stuck on the
  // current step. Reset to pick-mentor whenever React Router pushes a new
  // location with no `?session=` query param.
  useEffect(() => {
    if (replaySessionId) return;
    setStep('pick-mentor');
    setMentorId(null);
    setSpreadId('three_card');
    setCategoryId(null);
    setIntention('');
    setSession(null);
    setStreamText('');
    setError(null);
    setCompleteWarning(null);
    setInteractiveSessionId(null);
    setPoolSize(0);
    setPickedSlots([]);
    setRevealedCards(null);
    setRevealedIdx(new Set());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  // Replay a past session when arriving via `/reading?session=<id>` (from History).
  useEffect(() => {
    if (!replaySessionId) return;
    let cancelled = false;
    void (async () => {
      try {
        const s = await api.getSession(replaySessionId);
        if (cancelled) return;
        setSession(s);
        if (s.mentor_id) setMentorId(s.mentor_id);
        if (s.category_id) setCategoryId(s.category_id);
        if (s.intention) setIntention(s.intention);
        // Only show the read-only "done" view (interpretation, follow-ups,
        // rating panel) when the session actually completed. An abandoned
        // session in 'awaiting' / 'reading' / 'reflection' would otherwise
        // surface a rating panel that 409s on submit because the state
        // machine refuses to transition awaiting→complete.
        if (s.status === 'complete') {
          setStep('done');
        } else if (s.interpretation_text) {
          // M3 — interpretation streamed but /complete never landed.
          // Show the read-only view instead of stranding the user; warn
          // them so they understand why rating may briefly 409 (the
          // RatingPanel retries /complete on submit so this self-heals).
          setCompleteWarning('這次解讀的伺服器同步未完成。評分時將自動嘗試補上。');
          setStep('done');
        } else {
          setError('這次解讀沒有完成，請重新開始一次抽牌。');
          setStep('pick-mentor');
        }
      } catch (e) {
        if (cancelled) return;
        setError(formatApiError(e, '載入此次解讀失敗，請稍後再試。'));
        setStep('pick-mentor');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [replaySessionId]);

  /** STA-116 — start interactive draw: server allocates a pool and we
   * advance to pick-cards. K face-down placeholders are then rendered;
   * user picks K, hits 解讀, which calls ``commitPicks``. */
  const openInteractiveSession = async () => {
    if (!mentorId) return;
    // STA-152 — spread comes from the loaded catalog. Fall back to
    // the legacy 3-card if the server returned nothing (defensive).
    const spread = spreads.find((s) => s.id === spreadId) ?? spreads[0];
    if (!spread) {
      setError('沒有可用的牌陣，請稍後再試。');
      return;
    }
    setError(null);
    setStep('opening');
    setPickedSlots([]);
    setRevealedCards(null);
    setRevealedIdx(new Set());
    setStreamText('');
    try {
      const sess = await api.createInteractiveSession({
        mentor_id: mentorId,
        category_id: categoryId ?? undefined,
        intention: intention.trim(),
        // STA-152 — server reads spread_size from spreads.position_count
        // when spread_id is provided; we still send size as a fallback
        // for backward compat with older deploys.
        spread_size: spread.position_count,
        spread_id: spread.id,
      });
      track('reading_started', {
        mentor_id: mentorId,
        mode: mentor?.mode ?? 'unknown',
        interactive: true,
      });
      setInteractiveSessionId(sess.session_id);
      setPoolSize(sess.pool_size);
      setRequiredPicks(sess.spread_size);
      setStep('pick-cards');
    } catch (e: unknown) {
      const err = e as { status?: number };
      if (err.status === 429) {
        track('rate_limit_hit');
        setStep('rate-limited');
      } else {
        setStep('write-intention');
        setError(formatApiError(e, '建立占卜失敗，請稍後再試。'));
      }
    }
  };

  /** Toggle a slot in pickedSlots (until we hit ``requiredPicks``). */
  const togglePick = (slotIndex: number) => {
    setPickedSlots((prev) => {
      if (prev.includes(slotIndex)) {
        return prev.filter((s) => s !== slotIndex);
      }
      if (prev.length >= requiredPicks) return prev;
      return [...prev, slotIndex];
    });
  };

  /** Commit picks → SSE stream. First event is picks_committed (cards
   * revealed), subsequent are content deltas. */
  const commitPicks = async () => {
    if (!interactiveSessionId || pickedSlots.length !== requiredPicks) return;
    setError(null);
    setStep('committing');
    // M4 — cancel any prior commit stream (defensive; should not happen
    // in normal flow), then arm a fresh idle-timeout controller.
    commitSSERef.current?.cancel();
    const sse = makeSSEAbort(60_000);
    commitSSERef.current = sse;
    sse.bump();
    try {
      const res = await fetch(
        `/api/v1/readings/sessions/${interactiveSessionId}/interpret-picked`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...api.authHeaders(),
          },
          body: JSON.stringify({ picked_slots: pickedSlots }),
          signal: sse.signal,
        },
      );
      if (api.handleAuthFailure(res.status)) return;
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(`status=${res.status} body=${txt}`);
      }
      let acc = '';
      for await (const { event, data } of parseSSEEvents<any>(res.body!, () => sse.bump())) {
        if (event === 'picks_committed') {
          setRevealedCards(data.drawn_cards as DrawnCard[]);
          setStep('streaming');
          // Stagger reveal animation across cards.
          const N = (data.drawn_cards as DrawnCard[]).length;
          for (let i = 0; i < N; i++) {
            setTimeout(() => {
              setRevealedIdx((prev) => {
                if (prev.has(i)) return prev;
                const next = new Set(prev);
                next.add(i);
                return next;
              });
            }, 250 + i * 350);
          }
        } else if (event === 'content') {
          if (typeof data.delta === 'string') {
            acc += data.delta;
            setStreamText(acc);
          }
        } else if (event === 'error') {
          throw new Error(data.message || 'stream error');
        }
        // 'end' is informational — the for-await loop naturally finishes
        // when the server closes the response body.
      }
      // Stream drained — finalize the session server-side. This grants
      // completion Stardust (idempotent) and transitions the row to
      // ``complete``, which is the precondition for follow-ups.
      // M3 — try once, then retry once after a brief pause. /complete
      // is idempotent so the retry is safe. If both attempts fail we
      // still progress to step='done' (the user already read their
      // interpretation) but surface a non-fatal warning. RatingPanel
      // will attempt /complete again on submit, so this self-heals.
      let completed: SessionState | null = null;
      const tryComplete = async (): Promise<SessionState | null> => {
        try {
          const r = await fetch(
            `/api/v1/readings/sessions/${interactiveSessionId}/complete`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                ...api.authHeaders(),
              },
              body: JSON.stringify({ interpretation_text: acc || null }),
            },
          );
          if (api.handleAuthFailure(r.status)) return null;
          if (r.ok) return (await r.json()) as SessionState;
        } catch {
          // network failure — retry will catch it
        }
        return null;
      };
      completed = await tryComplete();
      if (!completed) {
        await new Promise((res) => setTimeout(res, 800));
        completed = await tryComplete();
      }
      if (!completed) {
        setCompleteWarning(
          '解讀已完成，但無法同步至伺服器。評分或追問時會自動補上記錄；如持續失敗，請稍後重新整理。',
        );
      }
      setSession(
        completed ?? {
          session_id: interactiveSessionId,
          status: 'reading',
          category_id: categoryId,
          mentor_id: mentorId,
          intention: intention.trim(),
          interpretation_text: acc || null,
          reflection_text: null,
          drawn_cards: revealedCards ?? [],
          followups: [],
          followup_meta: null,
          created_at: '',
          updated_at: '',
          completed_at: null,
        },
      );
      setStep('done');
      void refresh();
    } catch (e: unknown) {
      // M4 — distinguish "we aborted because unmount/cancel" from
      // "idle timeout fired" from "real error". On manual cancel
      // (component is going away anyway) we silently exit.
      const timeoutErr = commitSSERef.current?.reason();
      if (timeoutErr) {
        setError(timeoutErr.message);
        setStep('pick-cards');
      } else if (
        e instanceof DOMException && e.name === 'AbortError' && !timeoutErr
      ) {
        // Caller cancelled (unmount). Don't touch state.
        return;
      } else {
        setError(formatApiError(e, '解讀失敗，請稍後再試。'));
        setStep('pick-cards');
      }
    } finally {
      sse.cancel();
      if (commitSSERef.current === sse) commitSSERef.current = null;
    }
  };

  /** RE-P2 — post a follow-up; consume SSE and update local state. */
  const submitFollowup = async (question: string) => {
    if (!session) return;
    setFollowupBusy(true);
    setFollowupError(null);
    setFollowupStreamText('');
    // M4 — arm a fresh idle-timeout SSE controller.
    followupSSERef.current?.cancel();
    const sse = makeSSEAbort(60_000);
    followupSSERef.current = sse;
    sse.bump();
    try {
      const res = await fetch(
        `/api/v1/readings/sessions/${session.session_id}/followup`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...api.authHeaders(),
          },
          body: JSON.stringify({ question }),
          signal: sse.signal,
        },
      );
      if (api.handleAuthFailure(res.status)) return;
      if (!res.ok) {
        const txt = await res.text();
        let parsed: { detail?: { code?: string; message?: string } } | null = null;
        try { parsed = JSON.parse(txt); } catch { /* ignore */ }
        const code = parsed?.detail?.code;
        const message = parsed?.detail?.message || `status=${res.status}`;
        if (code === 'INSUFFICIENT_STARDUST') {
          setFollowupError('Stardust 餘額不足，前往帳號頁查看獲取方式。');
        } else if (code === 'WINDOW_EXPIRED') {
          setFollowupError('追問時效已過。');
        } else if (code === 'LIMIT_REACHED') {
          setFollowupError('本次占卜已用完 3 次追問。');
        } else {
          setFollowupError(message);
        }
        setFollowupBusy(false);
        return;
      }
      let acc = '';
      let followupId: string | null = null;
      let sequence = 0;
      for await (const { event, data } of parseSSEEvents<any>(res.body!, () => sse.bump())) {
        if (event === 'start') {
          followupId = data.followup_id;
          sequence = data.sequence;
        } else if (event === 'content' && typeof data.delta === 'string') {
          acc += data.delta;
          setFollowupStreamText(acc);
        } else if (event === 'error') {
          setFollowupError(data.message || 'Mentor 回應失敗。');
          // Refund happened server-side; refresh balance.
          void refresh();
          setFollowupBusy(false);
          setFollowupStreamText('');
          return;
        }
        // 'end' is informational — the for-await loop ends when the
        // server closes the response body.
      }
      // Drain finished. Append local follow-up + clear inputs.
      if (followupId && acc) {
        setSession((prev) => prev
          ? {
              ...prev,
              followups: [
                ...(prev.followups ?? []),
                {
                  id: followupId!,
                  sequence,
                  question,
                  answer_text: acc,
                  status: 'complete',
                  stardust_cost: session.followup_meta?.user_pays_zero ? 0 : (session.followup_meta?.cost_stardust ?? 50),
                  was_free: !!session.followup_meta?.user_pays_zero,
                  created_at: new Date().toISOString(),
                  completed_at: new Date().toISOString(),
                },
              ],
              followup_meta: prev.followup_meta
                ? {
                    ...prev.followup_meta,
                    used: prev.followup_meta.used + 1,
                    remaining: Math.max(0, prev.followup_meta.remaining - 1),
                  }
                : null,
            }
          : prev);
      }
      setFollowupStreamText('');
      setFollowupDraft('');
      void refresh();
    } catch (e: unknown) {
      const timeoutErr = followupSSERef.current?.reason();
      if (timeoutErr) {
        setFollowupError(timeoutErr.message);
      } else if (e instanceof DOMException && e.name === 'AbortError' && !timeoutErr) {
        // Caller cancelled (unmount). Don't touch state.
        return;
      } else {
        setFollowupError(formatApiError(e, '追問失敗，請稍後再試。'));
      }
    } finally {
      setFollowupBusy(false);
      sse.cancel();
      if (followupSSERef.current === sse) followupSSERef.current = null;
    }
  };

  if (step === 'rate-limited') {
    return (
      <div className="max-w-md mx-auto px-4 py-16 text-center">
        <h1 className="mp-display text-3xl text-tarot-gold mb-4">{t('reading.rate_limited_title')}</h1>
        <p className="text-tarot-muted mb-8">{t('reading.rate_limited_body')}</p>
        {paymentsEnabled ? (
          <Link to="/pricing" className="btn-primary">{t('reading.see_plans')}</Link>
        ) : (
          <button
            type="button"
            disabled
            aria-disabled="true"
            className="btn-primary opacity-60 cursor-not-allowed"
          >
            Premium 訂閱即將上線
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 py-6 sm:py-12">
      {step === 'pick-mentor' && (
        <div className="mb-8">
          <CheckinPill />
        </div>
      )}

      {step === 'pick-mentor' && (
        <div className="mb-8">
          <DailyOmen />
        </div>
      )}

      {step === 'pick-mentor' && catalogLoading && mentors.length === 0 && (
        <Loading text="載入導師…" />
      )}

      {step === 'pick-mentor' && !catalogLoading && mentors.length === 0 && !error && (
        <Section title={t('reading.pick_mentor')}>
          <div className="card text-center text-tarot-muted">
            目前沒有可用的導師，請稍後再試。
          </div>
        </Section>
      )}

      {step === 'pick-mentor' && mentors.length > 0 && (
        <Section title={t('reading.pick_mentor')}>
          {/* STA-151 — gate premium-only mentors behind subscription.
              Free users see only premium_only=false rows; subscribers
              see everything. (Filter `m.tags` chip + the premium gate
              both run client-side; server still authoritatively rejects
              premium mentor requests in the reading endpoint.) */}
          {(() => {
            const isPremium = user?.subscription?.status === 'active';
            const visible = mentors.filter((m) => isPremium || !m.premium_only);
            return (
              <>
                <MentorFilterChips
                  mentors={visible}
                  active={mentorFilterTag}
                  onChange={setMentorFilterTag}
                />
                {!isPremium && mentors.some((m) => m.premium_only) && (
                  <p className="text-xs text-tarot-muted mt-3">
                    部分老師需 Premium 才能使用。
                    {paymentsEnabled ? (
                      <Link to="/pricing" className="underline ml-1 text-tarot-gold">
                        升級看完整名單
                      </Link>
                    ) : (
                      <span className="ml-1 text-tarot-gold">（訂閱即將上線）</span>
                    )}
                  </p>
                )}
                <div className="grid md:grid-cols-2 gap-4 mt-4">
                  {visible
                    .filter((m) => !mentorFilterTag || m.tags.includes(mentorFilterTag))
                    .map((m, i) => (
                      <MentorCard
                        key={m.id}
                        m={m}
                        delay={i * 0.06}
                        onPick={() => {
                          if (m.online_status === 'offline') return;
                          setMentorId(m.id);
                          setStep('pick-spread');
                        }}
                      />
                    ))}
                </div>
              </>
            );
          })()}
        </Section>
      )}

      {step === 'pick-spread' && (
        <Section title={t('reading.pick_spread')}>
          <div className="grid md:grid-cols-2 gap-3">
            {spreads.map((s, i) => {
              const isActive = spreadId === s.id;
              return (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => setSpreadId(s.id)}
                  style={{ ['--list-delay' as string]: `${i * 0.05}s` }}
                  className={`card list-enter text-left transition-colors ${
                    isActive ? 'border-tarot-gold ring-1 ring-tarot-gold/30' : 'hover:border-tarot-gold/45'
                  }`}
                >
                  <div className="flex items-baseline justify-between gap-3">
                    <span className="mp-display text-xl text-tarot-gold">
                      {lang === 'zh' ? s.name_zh : s.name_en}
                    </span>
                  </div>
                  <p className="text-tarot-muted text-xs mt-1">
                    {t('reading.spread_positions', { n: s.position_count })}
                  </p>
                  <p className="text-tarot-text/85 text-sm mt-3">
                    {lang === 'zh' ? s.description_zh : s.description_en}
                  </p>
                  <SpreadPositions spread={s} />
                </button>
              );
            })}
          </div>
          <div className="flex justify-between mt-6">
            <button onClick={() => setStep('pick-mentor')} className="btn-ghost">
              {t('reading.back')}
            </button>
            <button
              type="button"
              onClick={() => setStep('pick-category')}
              disabled={!spreadId || spreads.length === 0}
              className="btn-primary"
            >
              {t('reading.next')}
            </button>
          </div>
        </Section>
      )}

      {step === 'pick-category' && (
        <Section title="選擇主題（可略過）">
          <div className="grid md:grid-cols-2 gap-3">
            {categories.map((c, i) => (
              <button
                key={c.id}
                onClick={() => {
                  setCategoryId(c.id);
                  setStep('write-intention');
                }}
                style={{ ['--list-delay' as string]: `${i * 0.05}s` }}
                className="card list-enter hover:border-tarot-purple text-left"
              >
                <div className="text-tarot-text">{c.display_name_zh}</div>
                {c.subtitle_zh && (
                  <div className="text-tarot-muted text-xs mt-1">
                    {c.subtitle_zh}
                  </div>
                )}
              </button>
            ))}
          </div>
          <button
            onClick={() => {
              setCategoryId(null);
              setStep('write-intention');
            }}
            className="mt-6 text-tarot-muted text-sm"
          >
            略過 →
          </button>
        </Section>
      )}

      {step === 'write-intention' && (() => {
        // Resolve template chips for the picked category. Empty when no
        // category is chosen (user clicked "略過") or admin hasn't
        // configured any. Cap at 6 visible to avoid the chip row
        // dominating the screen — admin can keep the rest at lower
        // sort_order to leave room.
        const picked = categoryId ? categories.find((c) => c.id === categoryId) : null;
        const chips = (picked?.templates ?? []).slice(0, 6);
        return (
          <Section title="今天想問什麼？">
            {chips.length > 0 && (
              <div className="mb-3">
                <p className="text-xs text-tarot-muted mb-2">
                  試試這個（按一下填入）
                </p>
                <div className="flex flex-wrap gap-2">
                  {chips.map((t) => (
                    <button
                      key={t.id}
                      onClick={() => setIntention(t.text_zh)}
                      className="text-sm px-3 py-1.5 rounded-full border border-tarot-purple/30 bg-tarot-card hover:border-tarot-purple text-tarot-text/90 hover:text-tarot-text text-left max-w-full"
                      title={t.text_zh}
                    >
                      <span className="line-clamp-1">{t.text_zh}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
            <textarea
              value={intention}
              onChange={(e) => setIntention(e.target.value)}
              placeholder="例如：我最近換工作的猶豫，到底該往哪走？"
              className="input min-h-[8rem]"
              maxLength={500}
            />
            {/* Compliance Baseline (STA-138) — privacy reassurance inline
                with the question input so the user reads it before they
                share sensitive content. */}
            <p className="text-xs text-tarot-muted mt-2 leading-relaxed">
              您的提問內容與占卜資料將受到嚴密保護，僅用於本次 AI 解牌計算。本站承諾不會外洩或作為其他行銷用途。詳見
              <a href="/privacy" target="_blank" rel="noreferrer" className="underline mx-1">隱私權政策</a>。
            </p>
            {error && <p className="text-red-400 text-sm mt-2">{error}</p>}
            <div className="flex justify-between mt-6">
              <button onClick={() => setStep('pick-category')} className="btn-ghost">
                ← 重選
              </button>
              <button
                onClick={() => {
                  setError(null);
                  setStep('centering');
                }}
                disabled={!intention.trim()}
                className="btn-primary"
              >
                下一步
              </button>
            </div>
          </Section>
        );
      })()}

      {step === 'centering' && (
        <Section title="靜心">
          <div className="card text-center">
            <p className="text-tarot-text/90 leading-loose">
              接下來，請閉上眼睛
              <br />
              心中默想著你的問題
            </p>
          </div>
          <div className="flex justify-between mt-8">
            <button
              onClick={() => setStep('write-intention')}
              className="btn-ghost"
            >
              ← 重新編輯
            </button>
            <button onClick={openInteractiveSession} className="btn-primary">
              開始抽牌
            </button>
          </div>
        </Section>
      )}

      {step === 'opening' && <Loading text="洗牌中…" />}

      {step === 'pick-cards' && (
        <Section title={`從 ${poolSize} 張中選 ${requiredPicks} 張`}>
          <div className="text-tarot-muted text-sm mb-4">
            {pickedSlots.length < requiredPicks
              ? `已選 ${pickedSlots.length} / ${requiredPicks}，請點選您直覺的牌`
              : '已選滿，按下「請老師解讀」送出'}
          </div>
          <PickCarousel
            poolSize={poolSize}
            requiredPicks={requiredPicks}
            pickedSlots={pickedSlots}
            onTogglePick={togglePick}
          />
          {error && <p className="text-red-400 text-sm mt-3">{error}</p>}
          <div className="flex justify-between mt-6">
            <button
              onClick={() => {
                setPickedSlots([]);
                setError(null);
              }}
              className="btn-ghost"
              disabled={pickedSlots.length === 0}
            >
              清除選擇
            </button>
            <button
              onClick={commitPicks}
              disabled={pickedSlots.length !== requiredPicks}
              className="btn-primary"
            >
              請老師解讀
            </button>
          </div>
        </Section>
      )}

      {step === 'committing' && <Loading text="老師正在凝視牌面…" />}

      {(step === 'streaming' || step === 'done') && (revealedCards || session) && (
        <div>
          <Section title={t('reading.your_cards')}>
            {(() => {
              const spread = spreads.find((s) => s.id === spreadId) ?? spreads[0];
              if (!spread) {
                return <div className="text-tarot-muted text-sm">…</div>;
              }
              const labels = lang === 'zh' ? spread.position_labels_zh : spread.position_labels_en;
              const cards = revealedCards ?? session?.drawn_cards ?? [];
              const cols = cards.length <= 3 ? 'grid-cols-3' : cards.length <= 5 ? 'grid-cols-5' : 'grid-cols-5 sm:grid-cols-5';
              return (
                <div className={`grid ${cols} gap-3 sm:gap-4 max-w-md mx-auto`}>
                  {cards.map((c, i) => (
                    <div key={c.id} className="flex flex-col items-center">
                      <div className="tarot-flip w-full">
                        <div
                          className={`tarot-flip-inner ${revealedIdx.has(i) || step === 'done' ? 'flipped' : ''}`}
                        >
                          <div className="tarot-back" />
                          <div className="tarot-face">
                            <div className="text-tarot-gold text-sm font-medium leading-tight">
                              {c.card_name}
                            </div>
                            {c.is_reversed && (
                              <div className="text-xs text-tarot-muted mt-1">逆位</div>
                            )}
                          </div>
                        </div>
                      </div>
                      {labels[i] && (
                        <div className="text-[10px] tracking-widest text-tarot-muted mt-1.5 text-center">
                          {labels[i]}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              );
            })()}
          </Section>

          <Section title={mentor?.name ? `${mentor.name} 的解讀` : '解讀'}>
            <div className="card whitespace-pre-wrap leading-relaxed text-tarot-text">
              {streamText ? (
                streamText
              ) : session?.interpretation_text ? (
                session.interpretation_text
              ) : step === 'streaming' ? (
                <div
                  className="tarot-stream-loading"
                  aria-live="polite"
                  aria-busy="true"
                >
                  <div className="tarot-stream-loading-line" />
                  <div className="tarot-stream-loading-line" />
                  <div className="tarot-stream-loading-line" />
                  <div className="tarot-stream-loading-hint">
                    <span className="dot" aria-hidden="true" />
                    {mentor?.name ? `${mentor.name} 正在凝神…` : '老師正在凝神…'}
                  </div>
                </div>
              ) : (
                '...'
              )}
            </div>
          </Section>

          {step === 'done' && completeWarning && (
            <div
              role="status"
              className="card border-yellow-500/40 bg-yellow-500/10 text-yellow-200 text-sm mb-6"
            >
              ⚠ {completeWarning}
            </div>
          )}

          {step === 'done' && session && (
            <FollowupPanel
              session={session}
              mentorName={mentor?.name}
              userStardust={user?.stardust ?? 0}
              draft={followupDraft}
              setDraft={setFollowupDraft}
              busy={followupBusy}
              streamText={followupStreamText}
              error={followupError}
              onSubmit={submitFollowup}
            />
          )}

          {step === 'done' && session && (
            <RatingPanel sessionId={session.session_id} mentorName={mentor?.name} />
          )}

          {step === 'done' && <Disclaimer />}

        </div>
      )}
    </div>
  );
}
